// Desktop UI route table — the wide, sidebar-driven layout.
//
// This is the existing React experience, unchanged. It renders inside the router
// and theme provider owned by src/App.jsx, and reads all data through src/core.

import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useApp } from '../../core/state/AppContext';
import { kioskLandingRoute } from '../../core/wall/kioskMode';

import AppLayout from './components/AppLayout';
import LoginScreen from './pages/LoginScreen';
import OtpScreen from './pages/OtpScreen';
import DesktopShell from './pages/DesktopShell';
import RoomsScreen from './pages/RoomsScreen';
import DeviceScreen from './pages/device/DeviceScreen';
import SchedulesScreen from './pages/SchedulesScreen';
import FavouritesScreen from './pages/FavouritesScreen';
import ScenesScreen from './pages/ScenesScreen';
import RoomScenesScreen from './pages/scenes/RoomScenesScreen';
import ProfileScreen from './pages/ProfileScreen';
import CompaniesScreen from './pages/CompaniesScreen';
import DeviceTypesScreen from './pages/DeviceTypesScreen';
import UserAccessScreen from './pages/UserAccessScreen';
import InstalledDevicesScreen from './pages/InstalledDevicesScreen';
import DeviceTrackingScreen from './pages/DeviceTrackingScreen';
import RfDevicesScreen from './pages/RfDevicesScreen';
import RfSignaturesScreen from './pages/RfSignaturesScreen';
import RfLogScreen from './pages/RfLogScreen';
import DeviceAdminScreen from './pages/DeviceAdminScreen';
import MqttConsoleScreen from './pages/MqttConsoleScreen';
import TuyaTestingScreen from './pages/TuyaTestingScreen';
import FirmwareScreen from './pages/FirmwareScreen';
import WallModeScreen from './pages/wall/WallModeScreen';
import WallPanelSetupScreen from '../shared/WallPanelSetupScreen';
import KioskSetupScreen from './pages/KioskSetupScreen';
import AdminToolsLayout from '../shared/AdminToolsLayout';
import AboutScreen from './pages/AboutScreen';

// ⚠️ NO BLE ROUTE HERE, deliberately. `/ble-test` was added to this shell and
// then removed: a desktop browser cannot drive Bluetooth usefully (see the note
// at the top of core/ble/bleClient), so the page could only ever report why it
// was unavailable. The BLE bench lives in the mobile shell — MobileRoutes.jsx —
// where the radio actually exists.

function Protected({ children }) {
  const { isAuthed, signingIn } = useApp();
  // Hold the route while a saved sign-in is in flight. Redirecting on the first
  // paint would show the login form to someone who is already being signed in,
  // and the form would then vanish under them a moment later.
  if (signingIn) return <SigningIn />;
  return isAuthed ? children : <Navigate to="/login" replace />;
}

function SigningIn() {
  return (
    <Box sx={{
      minHeight: '100vh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', gap: 2,
      bgcolor: 'background.default',
    }}
    >
      <CircularProgress />
      <Typography variant="body2" color="text.secondary">Signing you in…</Typography>
    </Box>
  );
}

export default function DesktopRoutes() {
  const { isAuthed, isSuperAdmin, canTuyaTest, signingIn } = useApp();

  // The admin tools, each gated HERE at its route and again inside the page —
  // the tab layout renders these as given and adds no permission of its own.
  // Tuya Testing is canTuyaTest (AllowTestingTuya), not IsSuperAdmin: the bench
  // operators granted it are not Super Admins. Firmware and Device Tracking
  // reflash hardware; Device Admin / User Access write ownership and access.
  const home = <Navigate to="/home" replace />;
  const adminScreens = {
    '/mqtt': isSuperAdmin ? <MqttConsoleScreen /> : home,
    '/device-tracking': isSuperAdmin ? <DeviceTrackingScreen /> : home,
    '/tuya-testing': canTuyaTest ? <TuyaTestingScreen /> : home,
    '/firmware': isSuperAdmin ? <FirmwareScreen /> : home,
    '/device-admin': isSuperAdmin ? <DeviceAdminScreen /> : home,
    '/user-access': isSuperAdmin ? <UserAccessScreen /> : home,
    '/installed': isSuperAdmin ? <InstalledDevicesScreen /> : home,
    '/device-types': isSuperAdmin ? <DeviceTypesScreen /> : home,
    '/companies': isSuperAdmin ? <CompaniesScreen /> : home,
    '/rf-log': isSuperAdmin ? <RfLogScreen /> : home,
    '/rf-signatures': isSuperAdmin ? <RfSignaturesScreen /> : home,
  };

  return (
    <Routes>
      {/* The login form must not appear while a saved sign-in is running, and
          must not appear at all to someone already signed in — landing on
          /login by bookmark or Back should take them home, not ask again. */}
      <Route
        path="/login"
        element={
          // eslint-disable-next-line no-nested-ternary
          signingIn ? <SigningIn />
            : isAuthed ? <Navigate to="/home" replace />
              : <LoginScreen />
        }
      />
      <Route path="/otp" element={<OtpScreen />} />

      {/* Kiosk full-screen — deliberately without the app header. */}
      {/* ⚠️ THERE IS ONE WALL EDITOR, and it is /wall/setup. A second designer
          (/wall/design, free-flow panels with mqtt+delay sequences) used to live
          alongside it, so "settings" meant two different screens depending on
          which button you had pressed. Removed rather than hidden — a route
          nobody links to is a screen nobody maintains. */}
      <Route path="/wall" element={<Protected><WallModeScreen /></Protected>} />

      {/* Everything else renders under the persistent header. */}
      <Route element={<Protected><AppLayout /></Protected>}>
        <Route path="/home" element={<DesktopShell />} />
        <Route path="/rooms/:locationId" element={<RoomsScreen />} />
        <Route path="/room/:roomId/scenes" element={<RoomScenesScreen />} />
        <Route path="/device/:uniqueNumber" element={<DeviceScreen />} />
        <Route path="/schedule/:uniqueNumber/:output" element={<SchedulesScreen />} />
        <Route path="/favourites" element={<FavouritesScreen />} />
        <Route path="/scenes" element={<ScenesScreen />} />
        <Route path="/profile" element={<ProfileScreen />} />
        <Route path="/about" element={<AboutScreen />} />
        {/* Design the current location's fixed wall sheet. */}
        <Route path="/wall/setup" element={<WallPanelSetupScreen />} />
        <Route path="/wall/kiosk" element={<KioskSetupScreen />} />
        {/* RF sensors — no unique number, so they are not devices anywhere else.
            NOT admin-gated: a homeowner adds their own door and safe sensors.
            The screen scopes a non-admin to their own locations. */}
        <Route path="/rf-devices" element={<RfDevicesScreen />} />
        {/* ── The admin tools, as tabs (ui/shared/AdminToolsLayout) ─────────
            One layout route over all of them, so switching is a tab tap and a
            tool once opened keeps its state while you move between them. The
            gates did NOT move: each element in `adminScreens` carries its own
            route gate, and every page checks again inside. */}
        <Route element={<AdminToolsLayout shell="desktop" screens={adminScreens} />}>
          {Object.keys(adminScreens).map((path) => (
            <Route key={path} path={path} element={null} />
          ))}
        </Route>
      </Route>

      {/* A wall tablet lands on its panel instead of the normal home. Only the
            LANDING route is redirected — forcing /wall on every navigation would
            make the designer unreachable from the very tablet it configures. */}
        <Route
          path="*"
          element={
            // eslint-disable-next-line no-nested-ternary
            !isAuthed ? <Navigate to="/login" replace />
              : <Navigate to={kioskLandingRoute() || '/home'} replace />
          }
        />
    </Routes>
  );
}
